/**
 * Form validation utilities
 */

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether email is valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate Pakistani phone number
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - Whether phone number is valid
 */
export const isValidPhone = (phone) => {
  // Accept formats: 03001234567, +923001234567, 92 300 1234567, etc.
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  const phoneRegex = /^(\+92|92|0)?3[0-9]{9}$/;
  return phoneRegex.test(cleanPhone);
};

/**
 * Validate required field
 * @param {string} value - Value to validate
 * @returns {boolean} - Whether value is not empty
 */
export const isRequired = (value) => {
  return value !== undefined && value !== null && value.toString().trim() !== '';
};

/**
 * Validate minimum length
 * @param {string} value - Value to validate
 * @param {number} minLength - Minimum length required
 * @returns {boolean} - Whether value meets minimum length
 */
export const hasMinLength = (value, minLength) => {
  return value && value.length >= minLength;
};

/**
 * Validate maximum length
 * @param {string} value - Value to validate
 * @param {number} maxLength - Maximum length allowed
 * @returns {boolean} - Whether value is within maximum length
 */
export const hasMaxLength = (value, maxLength) => {
  return !value || value.length <= maxLength;
};

/**
 * Validate date is not in the past
 * @param {string} dateString - Date string to validate
 * @returns {boolean} - Whether date is today or in the future
 */
export const isNotPastDate = (dateString) => {
  if (!dateString) return false;
  const selectedDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selectedDate >= today;
};

/**
 * Validate appointment form
 * @param {Object} values - Form values
 * @returns {Object} - Validation errors
 */
export const validateAppointmentForm = (values) => {
  const errors = {};

  // Name validation
  if (!isRequired(values.name)) {
    errors.name = 'Full name is required';
  } else if (!hasMinLength(values.name, 2)) {
    errors.name = 'Name must be at least 2 characters';
  } else if (!hasMaxLength(values.name, 100)) {
    errors.name = 'Name must be less than 100 characters';
  }

  // Phone validation
  if (!isRequired(values.phone)) {
    errors.phone = 'Phone number is required';
  } else if (!isValidPhone(values.phone)) {
    errors.phone = 'Please enter a valid Pakistani phone number';
  }

  // Email validation (optional but must be valid if provided)
  if (values.email && !isValidEmail(values.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Gender validation
  if (!isRequired(values.gender)) {
    errors.gender = 'Please select your gender';
  }

  // Service validation
  if (!isRequired(values.service)) {
    errors.service = 'Please select a service';
  }

  // Date validation
  if (!isRequired(values.date)) {
    errors.date = 'Please select a preferred date';
  } else if (!isNotPastDate(values.date)) {
    errors.date = 'Please select a date from today onwards';
  }

  // Time validation
  if (!isRequired(values.time)) {
    errors.time = 'Please select a preferred time';
  }

  return errors;
};

/**
 * Validate contact form
 * @param {Object} values - Form values
 * @returns {Object} - Validation errors
 */
export const validateContactForm = (values) => {
  const errors = {};

  // Name validation
  if (!isRequired(values.name)) {
    errors.name = 'Your name is required';
  } else if (!hasMinLength(values.name, 2)) {
    errors.name = 'Name must be at least 2 characters';
  }

  // Email validation
  if (!isRequired(values.email)) {
    errors.email = 'Email address is required';
  } else if (!isValidEmail(values.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Phone validation (optional but must be valid if provided)
  if (values.phone && !isValidPhone(values.phone)) {
    errors.phone = 'Please enter a valid phone number';
  }

  // Subject validation
  if (!isRequired(values.subject)) {
    errors.subject = 'Subject is required';
  } else if (!hasMinLength(values.subject, 5)) {
    errors.subject = 'Subject must be at least 5 characters';
  }

  // Message validation
  if (!isRequired(values.message)) {
    errors.message = 'Message is required';
  } else if (!hasMinLength(values.message, 20)) {
    errors.message = 'Message must be at least 20 characters';
  } else if (!hasMaxLength(values.message, 1000)) {
    errors.message = 'Message must be less than 1000 characters';
  }

  return errors;
};
